import { ReactNode, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@bri/shared-components'
import {
  HomeIcon,
  UsersIcon,
  BuildingOfficeIcon,
  FolderIcon,
  PresentationChartBarIcon,
  DocumentTextIcon,
  WrenchScrewdriverIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'

interface AdminLayoutProps {
  children: ReactNode
}

function HeaderBadge() {
  return (
    <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-lg bg-admin-accent">
      B
    </span>
  )
}

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Users', href: '/users', icon: UsersIcon },
  { name: 'Workspaces', href: '/workspaces', icon: BuildingOfficeIcon },
  { name: 'Projects', href: '/projects', icon: FolderIcon },
  { name: 'Qual Usage', href: '/qual-usage', icon: PresentationChartBarIcon },
  { name: 'Logs', href: '/logs', icon: DocumentTextIcon },
  { name: 'Maintenance', href: '/maintenance', icon: WrenchScrewdriverIcon },
]

export default function AdminLayout({ children }: AdminLayoutProps) {
  const { user, logout } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  return (
    <div className="min-h-screen bg-admin-bg">
      {/* Mobile sidebar */}
      <div
        className={clsx(
          'fixed inset-0 z-50 lg:hidden',
          sidebarOpen ? 'block' : 'hidden'
        )}
      >
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
        
        {/* Sidebar */}
        <div className="fixed inset-y-0 left-0 w-64 bg-admin-surface border-r border-admin-border">
          <div className="flex items-center justify-between h-16 px-4 border-b border-admin-border">
            <Link
              to="/"
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-3 rounded-md focus:outline-none focus:ring-2 focus:ring-admin-accent focus:ring-offset-2"
              aria-label="Go to admin home"
            >
              <HeaderBadge />
              <span className="font-display font-bold text-lg">Admin</span>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-md hover:bg-admin-border"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          
          <nav className="p-4 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                  location.pathname === item.href
                    ? 'bg-admin-accent text-white'
                    : 'text-admin-muted hover:text-admin-text hover:bg-admin-border'
                )}
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
        <div className="flex grow flex-col bg-admin-surface border-r border-admin-border">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 h-16 px-6 border-b border-admin-border rounded-md focus:outline-none focus:ring-2 focus:ring-admin-accent focus:ring-offset-2"
            aria-label="Go to admin home"
          >
            <HeaderBadge />
            <span className="font-display font-bold text-lg">Admin</span>
          </Link>
          
          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                  location.pathname === item.href || 
                  (item.href !== '/' && location.pathname.startsWith(item.href))
                    ? 'bg-admin-accent text-white'
                    : 'text-admin-muted hover:text-admin-text hover:bg-admin-border'
                )}
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </Link>
            ))}
          </nav>
          
          {/* User section */}
          <div className="p-4 border-t border-admin-border">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-admin-accent flex items-center justify-center text-white font-medium">
                {user?.email?.[0]?.toUpperCase() || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.email}</p>
                <p className="text-xs text-admin-muted">Platform Admin</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-admin-muted hover:text-admin-text hover:bg-admin-border rounded-md transition-colors"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 items-center gap-x-4 border-b border-admin-border bg-admin-bg px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="lg:hidden p-2 -m-2 text-admin-muted"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          
          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex items-center gap-x-4 lg:gap-x-6 ml-auto">
              {/* Add notification bell or other top-bar items here */}
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  )
}




